export const DEFAULT_FOLLOW_BACK_RATIO_MIN = 0.01;
/**
 * isRelationshipState
 *
 * Type guard for the Socials relationship state filter.
 *
 * @param {unknown} value - Raw query param value.
 * @returns {value is SocialsHandleRelationshipState} True when valid.
 *
 */
const isRelationshipState = (value) => value === "FOLLOWER" ||
    value === "FOLLOWING" ||
    value === "MUTUAL" ||
    value === "NONE";
/**
 * parseOptionalNumber
 *
 * Parse a numeric query param. Returns undefined for empty/invalid inputs.
 *
 * @param {string | null} value - Raw query param.
 * @returns {number | undefined} Parsed number, or undefined.
 *
 */
const parseOptionalNumber = (value) => {
    if (!value)
        return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
};
/**
 * parseOptionalInteger
 *
 * Parse an integer query param. Returns undefined for empty/invalid inputs.
 *
 * @param {string | null} value - Raw query param.
 * @returns {number | undefined} Parsed integer, or undefined.
 *
 */
const parseOptionalInteger = (value) => {
    if (!value)
        return undefined;
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : undefined;
};
/**
 * parseOptionalBoolean
 *
 * Parse a boolean query param encoded as "true" or "false".
 *
 * @param {string | null} value - Raw query param.
 * @returns {boolean | undefined} Parsed boolean, or undefined.
 *
 */
const parseOptionalBoolean = (value) => {
    if (value === null || value === "")
        return undefined;
    if (value === "true")
        return true;
    if (value === "false")
        return false;
    return undefined;
};
/**
 * parseTopicTags
 *
 * Parse a comma-separated list of tags from a query param.
 *
 * @param {string | null} value - Raw query param.
 * @returns {string[] | undefined} Normalized tag list, or undefined.
 *
 */
const parseTopicTags = (value) => {
    const tags = (value ?? "")
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);
    return tags.length ? tags : undefined;
};
/**
 * toNumberOrZero
 *
 * Parse a non-negative integer from a query param, defaulting to zero.
 *
 * @param {string | null} value - Raw query param.
 * @returns {number} Non-negative integer.
 *
 */
const toNumberOrZero = (value) => {
    if (!value)
        return 0;
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};
/**
 * getPageFromCursor
 *
 * Convert an offset cursor into a 1-indexed page number.
 *
 * @param {string | null} cursor - Offset cursor ("startAt") from the URL.
 * @param {number} limit - Page size.
 * @returns {number} 1-indexed page number.
 *
 */
export const getPageFromCursor = (cursor, limit) => Math.floor(toNumberOrZero(cursor) / limit) + 1;
/**
 * parseFiltersFromSearchParams
 *
 * Convert URL search params into a Socials handles wave filters object.
 *
 * @param {URLSearchParams} searchParams - Current URL params.
 * @returns {SocialsHandlesWaveFilters} Parsed filter values.
 *
 */
export const parseFiltersFromSearchParams = (searchParams) => {
    const relationshipStateRaw = searchParams.get("relationshipState");
    const relationshipState = isRelationshipState(relationshipStateRaw)
        ? relationshipStateRaw
        : undefined;
    const followBackRatioMinParam = searchParams.get("followBackRatioMin");
    let followBackRatioMin;
    if (followBackRatioMinParam === null) {
        followBackRatioMin = DEFAULT_FOLLOW_BACK_RATIO_MIN;
    }
    else if (followBackRatioMinParam === "") {
        followBackRatioMin = null;
    }
    else {
        followBackRatioMin = parseOptionalNumber(followBackRatioMinParam);
    }
    return {
        followBackRatioMax: parseOptionalNumber(searchParams.get("followBackRatioMax")),
        followBackRatioMin,
        followerCountMin: parseOptionalInteger(searchParams.get("followerCountMin")),
        followingCountMin: parseOptionalInteger(searchParams.get("followingCountMin")),
        followsYou: parseOptionalBoolean(searchParams.get("followsYou")),
        isIgnored: parseOptionalBoolean(searchParams.get("isIgnored")),
        outboundAmplificationMax: parseOptionalNumber(searchParams.get("outboundAmplificationMax")),
        outboundAmplificationMin: parseOptionalNumber(searchParams.get("outboundAmplificationMin")),
        outboundReshareRateMax: parseOptionalNumber(searchParams.get("outboundReshareRateMax")),
        outboundReshareRateMin: parseOptionalNumber(searchParams.get("outboundReshareRateMin")),
        recencyDays: parseOptionalInteger(searchParams.get("recencyDays")),
        accountCreatedSinceDays: parseOptionalInteger(searchParams.get("accountCreatedSinceDays")),
        latestInteractedDaysMin: parseOptionalInteger(searchParams.get("latestInteractedDaysMin")),
        relationshipState,
        scoreMax: parseOptionalNumber(searchParams.get("scoreMax")),
        scoreMin: parseOptionalNumber(searchParams.get("scoreMin")),
        topicTags: parseTopicTags(searchParams.get("topicTags"))
    };
};
/**
 * buildSearchParamsFromFilters
 *
 * Convert a Socials handles wave filters object into URL search params.
 *
 * @param {SocialsHandlesWaveFilters} filters - Current filter state.
 * @returns {URLSearchParams} Search params for the URL.
 *
 */
export const buildSearchParamsFromFilters = (filters) => {
    const params = new URLSearchParams();
    const toFollowBackRatioMinString = (value) => {
        if (value === null)
            return "";
        if (value === undefined)
            return String(DEFAULT_FOLLOW_BACK_RATIO_MIN);
        return String(value);
    };
    const toOptionalString = (value) => value === null || value === undefined ? undefined : String(value);
    const toOptionalBooleanString = (value) => {
        if (value === null || value === undefined)
            return undefined;
        return value ? "true" : "false";
    };
    const values = [
        ["relationshipState", filters.relationshipState ?? undefined],
        [
            "topicTags",
            filters.topicTags?.length ? filters.topicTags.join(",") : undefined
        ],
        ["recencyDays", toOptionalString(filters.recencyDays)],
        [
            "accountCreatedSinceDays",
            toOptionalString(filters.accountCreatedSinceDays)
        ],
        [
            "latestInteractedDaysMin",
            toOptionalString(filters.latestInteractedDaysMin)
        ],
        ["scoreMin", toOptionalString(filters.scoreMin)],
        ["scoreMax", toOptionalString(filters.scoreMax)],
        [
            "followBackRatioMin",
            toFollowBackRatioMinString(filters.followBackRatioMin)
        ],
        ["followBackRatioMax", toOptionalString(filters.followBackRatioMax)],
        ["followerCountMin", toOptionalString(filters.followerCountMin)],
        ["followingCountMin", toOptionalString(filters.followingCountMin)],
        [
            "outboundAmplificationMin",
            toOptionalString(filters.outboundAmplificationMin)
        ],
        [
            "outboundAmplificationMax",
            toOptionalString(filters.outboundAmplificationMax)
        ],
        [
            "outboundReshareRateMin",
            toOptionalString(filters.outboundReshareRateMin)
        ],
        [
            "outboundReshareRateMax",
            toOptionalString(filters.outboundReshareRateMax)
        ],
        ["followsYou", toOptionalBooleanString(filters.followsYou)],
        ["isIgnored", toOptionalBooleanString(filters.isIgnored)]
    ];
    values.forEach(([key, value]) => {
        if (value !== undefined) {
            params.set(key, value);
        }
    });
    return params;
};
